import { isDarkMode } from "../../../utils/constants";
import { formatLargeNumbers } from "../utils";
/* -------------------- Robust date utilities (JS) -------------------- */

// Parse a variety of raw date inputs to a Date, or null if unparseable.
export function parseRawDate(raw, scale) {
  if (raw == null) return null;

  if (typeof raw === "number") {
    const ms = raw < 1e12 ? raw * 1000 : raw; // seconds vs ms
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }

  if (typeof raw === "string") {
    let s = raw.trim();
    if (s === "") return null;

    // Explicit UTC suffix -> keep as UTC
    if (/ UTC$/i.test(s)) {
      s = s.replace(/ UTC$/i, "Z").replace(" ", "T");
      const d = new Date(s);
      return isNaN(d.getTime()) ? null : d;
    }

    // Pure date "YYYY-MM-DD" -> pin to UTC midnight to avoid TZ day drift
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const d = new Date(s + "T00:00:00Z");
      return isNaN(d.getTime()) ? null : d;
    }

    // Naive datetime "YYYY-MM-DD HH:MM(:SS)?" or "YYYY-MM-DDTHH:MM(:SS)?"
    // Interpret as LOCAL wall-clock (no Z); build from components to avoid engine quirks.
    const m = s.match(
      /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/
    );
    if (m) {
      const [_, Y, M, D, h, mnt, ssec] = m;
      const d = new Date(
        Number(Y),
        Number(M) - 1,
        Number(D),
        Number(h),
        Number(mnt),
        Number(ssec ?? "0"),
        0
      ); // local time
      return isNaN(d.getTime()) ? null : d;
    }

    // ISO with timezone or Z -> let Date handle it
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }

  try {
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

export function stepMsForScale(scale) {
  if (scale === "1min") return 60 * 1000;
  if (scale === "5min") return 5 * 60 * 1000;
  return 24 * 60 * 60 * 1000; // default 1 day
}

export function formatDisplayFromDate(d, scale, timezone) {
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const noTimeScales = new Set(["1d", "1day", "1w", "1mo"]);

  // Keep your special daily format
  if (scale === "1day" || scale === "1d") {
    const months = [
      "Jan","Feb","Mar","Apr","May","Jun",
      "Jul","Aug","Sep","Oct","Nov","Dec",
    ];
    return `${months[d.getMonth()]} '${d.getFullYear().toString().slice(2)}`;
  }

  // Intraday scales show time
  if (!["1d", "1w", "1mo"].includes(scale)) {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: tz,
    }).format(d);
  }

  // Everything else (incl. 1w, 1mo/1m) -> date only
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "UTC",
  }).format(d);
}

export function formatHumanTitle(dateObj, scale, timezone) {
  if (!dateObj) return "";
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const noTimeScales = new Set(["1d", "1day", "1w", "1mo"]);

  const baseOpts = {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "2-digit",
    timeZone: tz,
  };
  const withTime = {
    ...baseOpts,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };

  const opts = noTimeScales.has(scale) ? baseOpts : withTime;
  const parts = new Intl.DateTimeFormat("en-GB", opts).formatToParts(dateObj);

  const get = (type) => parts.find((p) => p.type === type)?.value ?? "";
  const wd = get("weekday");
  const day = get("day");
  const mon = get("month");
  const yr = get("year");
  const hh = get("hour");
  const mm = get("minute");

  return noTimeScales.has(scale)
    ? `${wd} ${day} ${mon} '${yr}`
    : `${wd} ${day} ${mon} '${yr} ${hh}:${mm}`;
}

export function formatTooltipTitle(raw, scale, timezone) {
  const d = parseRawDate(raw, scale);
  if (!d) return String(raw ?? "");
  return formatHumanTitle(d, scale, timezone);
}

/**
 * Normalize raw date values to:
 *  - ISO label strings (monotonic, non-null)
 *  - display strings for ticks
 * Invalid/missing dates are forward-filled using scale’s step.
 * Optionally appends padRight blank labels for right-pan space.
 */
export function normalizeLabels(rawDates, scale, padRight = 0, timezone) {
  const step = stepMsForScale(scale);
  const normalizedDates = [];
  let lastMs = null;

  for (let i = 0; i < rawDates.length; i++) {
    const d = parseRawDate(rawDates[i], scale);
    if (d) {
      let cur = d.getTime();
      if (lastMs != null && cur <= lastMs) {
        cur = lastMs + step; // force strictly increasing
      }
      normalizedDates.push(new Date(cur));
      lastMs = cur;
    } else {
      const cur = lastMs != null ? lastMs + step : Date.now() + i * step;
      normalizedDates.push(new Date(cur));
      lastMs = cur;
    }
  }

  const labels = normalizedDates.map((d) => d.toISOString());
  const formatted = normalizedDates.map((d) => formatDisplayFromDate(d, scale, timezone));

  for (let j = 0; j < padRight; j++) {
    labels.push("");
    formatted.push("");
  }

  return { labels, formatted, msArray: normalizedDates.map((d) => d.getTime()) };
}

/** Snap a raw x-value (values1) to the nearest chart label at or after that time. */
export function snapToNearestLabel(rawX, msArray, labels, scale) {
  const d = parseRawDate(rawX, scale);
  if (!d || !msArray.length) return labels[0] || "";
  const t = d.getTime();
  const idx = msArray.findIndex((ms) => ms >= t);
  if (idx === -1) return labels[labels.length - 1] || "";
  return labels[idx];
}

/* -------------------- Hover overlays (JS) -------------------- */

const getOrCreateOverlayElement = (parent, className, baseStyle) => {
  let el = parent.querySelector(`.${className}`);
  if (!el) {
    el = document.createElement("div");
    el.className = className;
    Object.assign(el.style, baseStyle);
    parent.appendChild(el);
  }
  return el;
};

const getOrCreateHoverElements = (chart) => {
  const parent = chart.canvas.parentNode;
  parent.style.position = parent.style.position || "relative";

  const tooltipEl = getOrCreateOverlayElement(parent, "fixed-tl-tooltip", {
    position: "absolute",
    pointerEvents: "none",
    background: "rgba(0,0,0,0.8)",
    color: "#fff",
    borderRadius: "6px",
    padding: "6px 8px",
    font: "12px/1.2 system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
    whiteSpace: "nowrap",
    zIndex: 3,
    opacity: 0,
    transition: "opacity 80ms linear",
  });

  const crosshairEl = getOrCreateOverlayElement(parent, "chart-hover-crosshair", {
    position: "absolute",
    pointerEvents: "none",
    width: "1px",
    zIndex: 2,
    opacity: 0,
    transition: "opacity 80ms linear",
  });

  const dateEl = getOrCreateOverlayElement(parent, "chart-hover-date", {
    position: "absolute",
    pointerEvents: "none",
    borderRadius: "6px",
    padding: "3px 8px",
    font: "600 11px/1.2 system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
    whiteSpace: "nowrap",
    transform: "translateX(-50%)",
    zIndex: 3,
    opacity: 0,
    transition: "opacity 80ms linear",
  });

  return { parent, tooltipEl, crosshairEl, dateEl };
};

export const isForexAssetType = (assetType) => {
  const normalized = String(assetType || "")
    .trim()
    .toLowerCase();
  if (!normalized) return false;
  return [
    "forex",
    "forex_pairs",
    "currency_pair",
    "currency_pairs",
    "currency pair",
    "currency pairs",
  ].includes(normalized);
};

export const getPriceFractionDigits = (assetType) => {
  if (!isForexAssetType(assetType)) return null;
  return { minimumFractionDigits: 5, maximumFractionDigits: 5 };
};

export function externalTooltipHandler(ctx) {
  const { chart, tooltip } = ctx;
  const { parent, tooltipEl, crosshairEl, dateEl } = getOrCreateHoverElements(chart);

  if (!tooltip || tooltip.opacity === 0 || !tooltip.dataPoints?.length) {
    tooltipEl.style.opacity = "0";
    crosshairEl.style.opacity = "0";
    dateEl.style.opacity = "0";
    return;
  }

  tooltipEl.style.background = isDarkMode
    ? "rgba(6, 10, 16, 0.56)"
    : "rgba(255, 255, 255, 0.72)";
  tooltipEl.style.color = isDarkMode ? "#fff" : "#111";
  tooltipEl.style.border = isDarkMode
    ? "1px solid rgba(255,255,255,0.15)"
    : "1px solid rgba(0,0,0,0.12)";
  tooltipEl.style.boxShadow = isDarkMode
    ? "0 2px 8px rgba(0,0,0,0.6)"
    : "0 2px 8px rgba(0,0,0,0.15)";
  tooltipEl.style.backdropFilter = "blur(1px)";

  crosshairEl.style.background = isDarkMode
    ? "rgba(255,255,255,0.24)"
    : "rgba(31,41,55,0.28)";

  dateEl.style.background = isDarkMode
    ? "rgba(11,13,16,0.94)"
    : "rgba(255,255,255,0.96)";
  dateEl.style.color = isDarkMode ? "#e5e7eb" : "#111827";
  dateEl.style.border = isDarkMode
    ? "1px solid rgba(255,255,255,0.16)"
    : "1px solid rgba(0,0,0,0.12)";
  dateEl.style.boxShadow = isDarkMode
    ? "0 2px 8px rgba(0,0,0,0.45)"
    : "0 2px 8px rgba(15,23,42,0.16)";

  const dp = tooltip.dataPoints[0];
  const raw = dp.raw;
  const priceDigits = getPriceFractionDigits(chart?.options?.__assetType);
  const fmtPrice = (n, defaultDigits = 2) => {
    if (n == null || Number.isNaN(+n)) return "-";
    if (priceDigits) {
      return (+n).toLocaleString(undefined, priceDigits);
    }
    return (+n).toFixed(defaultDigits);
  };
  const fmtInt = (n) => n == null || Number.isNaN(+n) ? "-" : formatLargeNumbers(Math.round(+n));

  // Title: format human based on selected scale stored in options
  const selectedScale = chart.options.__selectedScale;
  const userTimezone = chart.options.__userTimezone;
  const rawTitle = (tooltip.title && tooltip.title[0]) || dp.label || "";
  const title = formatTooltipTitle(rawTitle, selectedScale, userTimezone);
  let html = "";
  if (
    raw &&
    typeof raw === "object" &&
    "o" in raw &&
    "h" in raw &&
    "l" in raw &&
    "c" in raw
  ) {
    let volStr = `V ${raw.v != null ? fmtInt(raw.v) : "—"}`;
    if (!raw.v) volStr = "";

    html = `
      <div>
        O ${fmtPrice(raw.o)}&nbsp;&nbsp;
        H ${fmtPrice(raw.h)}&nbsp;&nbsp;
        L ${fmtPrice(raw.l)}&nbsp;&nbsp;
        C ${fmtPrice(raw.c)}&nbsp;&nbsp;
        ${volStr}
      </div>
    `;
  } else {
    const y = dp.parsed?.y ?? raw?.y ?? raw;
    const seriesLabel = dp.dataset.label || "Price";
    html = `
      <div>${seriesLabel}: ${fmtPrice(y)}</div>
    `;
  }

  tooltipEl.innerHTML = html;
  tooltipEl.style.opacity = "1";

  const ca = chart.chartArea;
  const parentWidth = parent.clientWidth || chart.width || 0;
  const isCompactWidth = parentWidth > 0 && parentWidth < 560;
  const leftShift = isCompactWidth ? 68 : 92;
  const minLeft = ca.left + 8;
  let tooltipX = ca.left + leftShift;
  if (parentWidth > 0) {
    const tooltipWidth = tooltipEl.offsetWidth || 0;
    if (tooltipWidth > 0) {
      tooltipX = Math.max(
        minLeft,
        Math.min(parentWidth - tooltipWidth - 8, tooltipX),
      );
    }
  }
  tooltipEl.style.left = `${Math.max(minLeft, tooltipX)}px`;
  tooltipEl.style.top = "0px";

  const fallbackX =
    typeof tooltip.caretX === "number" ? tooltip.caretX : ca.left;
  const pointX =
    typeof dp?.element?.x === "number" ? dp.element.x : fallbackX;
  const x = Math.max(ca.left, Math.min(ca.right, pointX));

  crosshairEl.style.opacity = "1";
  crosshairEl.style.left = `${x}px`;
  crosshairEl.style.top = `${ca.top}px`;
  crosshairEl.style.height = `${Math.max(0, ca.bottom - ca.top)}px`;

  const hasDateTitle = Boolean(String(title || "").trim());
  if (hasDateTitle) {
    dateEl.innerText = title;
    dateEl.style.opacity = "1";
    dateEl.style.top = `${ca.bottom + 6}px`;
    dateEl.style.left = `${x}px`;

    const half = dateEl.offsetWidth / 2 + 6;
    if (parentWidth > 0 && Number.isFinite(half)) {
      const clampedX = Math.max(half, Math.min(parentWidth - half, x));
      dateEl.style.left = `${clampedX}px`;
    }
  } else {
    dateEl.style.opacity = "0";
  }
}



export const dedupeTickerOptions = (options = []) => {
  if (!Array.isArray(options)) return [];
  const seen = new Set();
  return options.reduce((acc, option) => {
    if (!option || option.id == null) return acc;
    const label = option.label ?? String(option.id);
    if (seen.has(label)) return acc;
    seen.add(label);
    acc.push({ ...option, id: String(option.id) });
    return acc;
  }, []);
};

export const getDenomination = (assetType) => {
  return ["indices"].includes(assetType) || isForexAssetType(assetType) ? "" : "$"
}
